import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject } from "#customTypes/types"

export const PORTFOLIO_CATEGORY = {
  SELECTED: "selected",
  SUSTAINED: "sustained",
  PAUSED: "paused",
  UNASSIGNED: "unassigned",
} as const

export type PortfolioCategory =
  (typeof PORTFOLIO_CATEGORY)[keyof typeof PORTFOLIO_CATEGORY]

export const PORTFOLIO_CATEGORY_LABELS: Record<PortfolioCategory, string> = {
  [PORTFOLIO_CATEGORY.SELECTED]: "Selected Work",
  [PORTFOLIO_CATEGORY.SUSTAINED]: "Sustained Investigation",
  [PORTFOLIO_CATEGORY.PAUSED]: "Paused",
  [PORTFOLIO_CATEGORY.UNASSIGNED]: "Unassigned",
}

export const PORTFOLIO_CATEGORY_LIMITS: Record<PortfolioCategory, number> = {
  [PORTFOLIO_CATEGORY.SELECTED]: 5,
  [PORTFOLIO_CATEGORY.SUSTAINED]: 15,
  [PORTFOLIO_CATEGORY.PAUSED]: Infinity,
  [PORTFOLIO_CATEGORY.UNASSIGNED]: Infinity,
}

export const PORTFOLIO_REVIEW_STATUS = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const

export type PortfolioReviewStatus =
  (typeof PORTFOLIO_REVIEW_STATUS)[keyof typeof PORTFOLIO_REVIEW_STATUS]

export const PORTFOLIO_AUTHOR_KIND = {
  STUDENT: "STUDENT",
  TEACHER: "TEACHER",
} as const

export type PortfolioAuthorKind =
  (typeof PORTFOLIO_AUTHOR_KIND)[keyof typeof PORTFOLIO_AUTHOR_KIND]

export interface PortfolioReferenceItem {
  id: Uuid
  imageUrl: string
  title?: string
  notes?: string
  teacherComment?: string
}

/** Reference row persisted to Firestore (no teacher feedback text in v1). */
export interface PortfolioReferenceStored {
  id: Uuid
  imageUrl: string
  title?: string
  notes?: string
}

export interface PortfolioStatementReview {
  value?: string
  status?: PortfolioReviewStatus
  submittedAt?: string
  submittedBy?: Uuid
  reviewedAt?: string
  reviewedBy?: Uuid
  teacherFeedback?: string
}

/** Statement fields persisted to Firestore (no teacherFeedback in v1). */
export interface PortfolioStatementStored {
  value?: string
  status?: PortfolioReviewStatus
  submittedAt?: string
  submittedBy?: Uuid
  reviewedAt?: string
  reviewedBy?: Uuid
}

export interface PortfolioProfile {
  canvaLink?: string
  sustainedInquiryQuestion?: PortfolioStatementReview
  sustainedPracticeEvidence?: PortfolioStatementReview
}

export interface PortfolioProfileStored {
  canvaLink?: string
  sustainedInquiryQuestion?: PortfolioStatementStored
  sustainedPracticeEvidence?: PortfolioStatementStored
}

export type PortfolioStageStatus =
  | "not_started"
  | "in_progress"
  | "review"
  | "complete"
  | "has_feedback"

/** Stage row persisted to Firestore. */
export interface PortfolioStageStored {
  id: Uuid
  label: string
  status: PortfolioStageStatus
  dueDate: string
  artworkId?: Uuid
  imageUrl: string
  medium?: string
  notes?: string
  feedback?: string
  sortIndex?: number
  planningGroup?: string
  lastActivityAt?: string
  createdAt?: string
  createdBy?: Uuid
  customFields?: Record<string, string>
}

/**
 * Project blob persisted under `projects[id]` including nested maps.
 *
 * Ordering (JARVIS-126): the display order is "category, then
 * `categorySortIndex` within the category". Indexes are per category, so
 * moving a project between categories only renumbers the two affected groups.
 * `StudentPortfolioConstructor.projectOrder` is still written on every save as
 * the flattened arrangement, for readers (mobile, older code) that want one
 * list. A project without `categorySortIndex` sorts after indexed siblings,
 * in `projectOrder` position.
 *
 * Retired: `planningPriorityRank`. Some stored docs (and the fixture) still
 * carry it; it is ignored on read and dropped on the next full-document write.
 * Priority membership AND order live in `priorityLaneProjectIds` alone.
 */
export interface PortfolioProjectStored {
  id: Uuid
  classId?: Uuid
  title: string
  category: PortfolioCategory
  coverImageUrl: string
  notes?: string
  planningGroup?: string
  /** 0-based position within `category`. See the interface doc. */
  categorySortIndex?: number
  /**
   * References into the shared artwork tag dictionary
   * (`DOC_TYPES.ARTWORK_TAGS`, `ArtworkTagConstructor`) — "Oil paint",
   * "Identity", "Series A". IDS, not text: renaming a tag centrally has to
   * stay possible. Deliberately NOT `DOC_TYPES.TAGS`, which holds student/user
   * logistics tags surfaced in student and user pickers. Stored as `null` when
   * empty — `cleanFormData` turns `undefined`/`[]` into `null`.
   */
  tagIds?: Uuid[]
  lastActivityAt?: string
  createdAt?: string
  createdBy?: Uuid
  dimensions?: string
  materials?: string
  processes?: string
  ideas?: string
  stages: Record<Uuid, PortfolioStageStored>
  references?: Record<Uuid, PortfolioReferenceStored>
}

export interface PortfolioProject {
  id: Uuid
  classId: Uuid
  category: PortfolioCategory
  name: string
  sortIndex: number
  dueDate?: string
  notes?: string
  dimensions?: string
  materials?: string
  processes?: string
  ideas?: string
  references?: Record<Uuid, PortfolioReferenceItem>
}

export interface PortfolioMeta {
  ideas?: string
  materials?: string
  process?: string
  processes?: string
  citation?: string
  dimensions?: string
  referenceLinks?: string
  inPortfolio?: boolean
}

export interface PortfolioTimelineEntryStored {
  id: Uuid
  type: "event" | "comment"
  createdAt: string
  createdBy: Uuid
  /** Defaults to TEACHER when missing (legacy rows). */
  authorKind?: PortfolioAuthorKind
  message: string
  projectId?: Uuid
  eventScope?: "project" | "item"
  eventEntityId?: Uuid
  eventFieldKey?: string
  eventNextValue?: string
}

export interface StudentPortfolioConstructor extends DatabaseObject {
  id: Uuid
  studentId: Uuid
  profile: PortfolioProfileStored
  projects: Record<Uuid, PortfolioProjectStored>
  /** Flattened arrangement: category order, then `categorySortIndex`. */
  projectOrder: Uuid[]
  /**
   * The priority lane, in lane order. Membership and order in one field —
   * "a set of projects separated from the rest". Max PORTFOLIO_PRIORITY_LANE_MAX.
   */
  priorityLaneProjectIds: Uuid[]
  timelineEntries: PortfolioTimelineEntryStored[]
}

export interface PortfolioCommentConstructor extends DatabaseObject {
  id: Uuid
  studentId: Uuid
  classId: Uuid
  authorId: Uuid
  /** Defaults to TEACHER when missing (legacy rows). */
  authorKind?: PortfolioAuthorKind
  message: string
}
